import {
  isHiddenOrderType,
  isOrderType,
  ORDER_TYPE_IDS,
  TASK_TYPE_DISCIPLINE_MAP,
  type TaskTypeId,
} from '../../constants/visitTypes';
import type { DisciplineType } from '../../services/api/models/DisciplineType';
import {
  optionTypeId,
  withAliasOptions,
  type TaskTypeOption,
} from '../../hooks/useVisitTypeOptions';

function isCreatableTaskTypeOption(option: TaskTypeOption): boolean {
  const typeId = optionTypeId(option);
  if (isOrderType(typeId)) {
    return !isHiddenOrderType(typeId);
  }
  return !option.is_legacy;
}
import type {
  MiscTaskTypeId,
  OrderTypeId,
  PatientCommunicationTypeId,
  TaskListResponse,
  VisitTypeId,
} from '../../services/api/types.gen';
import { PATIENT_COMMUNICATION_TYPES } from '../patient/patientCommunicationTypes';
import { MISC_TASK_TYPES } from '../../constants/miscTaskTypes';

export type TaskTypeFilterValue =
  | 'all'
  | 'nursing'
  | 'all_therapy'
  | 'pt'
  | 'ot'
  | 'st'
  | 'msw'
  | 'hha'
  | 'orders'
  | 'comm'
  | 'misc';

export type TaskTypeFilterTab = {
  value: TaskTypeFilterValue;
  label: string;
  tooltip: string;
};

export type TherapyDiscipline = 'PT' | 'OT' | 'ST';

type TaskTypeClassificationCategory =
  | 'orders'
  | 'nursing'
  | 'therapy'
  | 'msw'
  | 'hha'
  | 'visit'
  | 'comm'
  | 'misc'
  | 'unknown';

export type TaskTypeClassification = {
  category: TaskTypeClassificationCategory;
  therapyDiscipline: TherapyDiscipline | null;
  aliases: string[];
};

// Creatable task kinds distinguish the Add Task picker surface from the
// persisted task rows: visits/orders are backed by visit/order APIs, while
// communications are only creatable through the patient communication API.
// Non-creatable task kinds are row-only values such as generic legacy visits or
// unknown task types that can appear in existing task data but should not be
// offered as creation options.
export type CreatableTaskKind = 'visit_or_order' | 'communication' | 'misc';

export type CreatableTaskTypeOption =
  | (TaskTypeOption & { taskKind?: 'visit_or_order' })
  | {
      id: PatientCommunicationTypeId;
      display_name: PatientCommunicationTypeId;
      taskKind: 'communication';
    }
  | {
      id: MiscTaskTypeId;
      display_name: MiscTaskTypeId;
      taskKind: 'misc';
    };

export const TASK_TYPE_FILTER_TABS: ReadonlyArray<TaskTypeFilterTab> =
  Object.freeze([
    { value: 'all', label: 'All', tooltip: 'Show all task types' },
    { value: 'nursing', label: 'Nursing', tooltip: 'Show nursing visits' },
    {
      value: 'all_therapy',
      label: 'Therapy',
      tooltip: 'Therapy',
    },
    {
      value: 'pt',
      label: 'PT',
      tooltip: 'Physical Therapy',
    },
    {
      value: 'ot',
      label: 'OT',
      tooltip: 'Occupational Therapy',
    },
    {
      value: 'st',
      label: 'ST',
      tooltip: 'Speech Therapy',
    },
    {
      value: 'hha',
      label: 'HHA',
      tooltip: 'Home Health Aide',
    },
    {
      value: 'msw',
      label: 'MSW',
      tooltip: 'Medical Social Work',
    },
    {
      value: 'orders',
      label: 'Orders',
      tooltip: 'Medical Orders',
    },
    {
      value: 'comm',
      label: 'Comm',
      tooltip: 'Patient Communications',
    },
    {
      value: 'misc',
      label: 'Misc',
      tooltip: 'Miscellaneous Tasks',
    },
  ]);

export const TASK_TYPE_CREATABLE_FILTER_TABS: ReadonlyArray<TaskTypeFilterTab> =
  Object.freeze(
    TASK_TYPE_FILTER_TABS.map(tab =>
      tab.value === 'all_therapy'
        ? { ...tab, label: 'Therapy' }
        : tab.value === 'comm'
          ? { ...tab, label: 'Comms' }
          : tab
    )
  );

export const TASK_TYPE_VISIT_FILTER_TABS: ReadonlyArray<TaskTypeFilterTab> =
  Object.freeze(
    TASK_TYPE_FILTER_TABS.filter(
      tab =>
        tab.value !== 'orders' && tab.value !== 'comm' && tab.value !== 'misc'
    )
  );

export const DEFAULT_TASK_TYPE_FILTER: TaskTypeFilterValue = 'all';

const FILTER_SUMMARIES: Record<TaskTypeFilterValue, string> = {
  all: 'All task types',
  nursing: 'Nursing',
  all_therapy: 'Therapy',
  pt: 'PT',
  ot: 'OT',
  st: 'ST',
  msw: 'MSW',
  hha: 'HHA',
  orders: 'Orders',
  comm: 'Comm',
  misc: 'Misc',
};

const OASIS_SUFFIX_ALIAS: Record<string, string> = {
  START_OF_CARE: 'SOC',
  RESUMPTION_OF_CARE: 'ROC',
  RECERTIFICATION: 'REC',
  DISCHARGE: 'DC',
  FOLLOW_UP: 'FUP',
  TRANSFER: 'TF',
  DEATH: 'DD',
};
const EXACT_SEARCH_TOKENS = new Set([
  'pt',
  'ot',
  'st',
  'sn',
  'soc',
  'roc',
  'rec',
  'dc',
  'fup',
  'tf',
  'dd',
  'msw',
  'hha',
]);

export function createDefaultTaskTypeFilter(): TaskTypeFilterValue {
  return DEFAULT_TASK_TYPE_FILTER;
}

const FILTER_BY_DISCIPLINE: Record<DisciplineType, TaskTypeFilterValue> = {
  SN: 'nursing',
  PT: 'pt',
  OT: 'ot',
  ST: 'st',
  MSW: 'msw',
  HHA: 'hha',
};

/**
 * The tab a clinician's own work lives on, from the disciplines their titles
 * license. The Episode Manager opens here so a PT does not have to
 * filter down to PT on every patient.
 *
 * Takes the first discipline rather than trying to merge several: someone
 * holding two titles has no single right answer, and `disciplinesForTitles`
 * already returns a stable order, so the same person always lands on the same
 * tab. Falls back to the default (All) for anyone with no clinical title --
 * office staff, schedulers, admins.
 */
export function taskTypeFilterForDisciplines(
  disciplines: readonly DisciplineType[] | null | undefined
): TaskTypeFilterValue {
  const first = disciplines?.[0];
  if (!first) return DEFAULT_TASK_TYPE_FILTER;
  return FILTER_BY_DISCIPLINE[first] ?? DEFAULT_TASK_TYPE_FILTER;
}

export function hasActiveTaskTypeFilter(filter: TaskTypeFilterValue): boolean {
  return filter !== DEFAULT_TASK_TYPE_FILTER;
}

export function isTaskTypeFilterDefault(filter: TaskTypeFilterValue): boolean {
  return !hasActiveTaskTypeFilter(filter);
}

export function getTaskTypeFilterSummary(filter: TaskTypeFilterValue): string {
  return FILTER_SUMMARIES[filter];
}

function normalizeText(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function getSearchableTokens(value: string): string[] {
  const normalized = normalizeText(value);
  return normalized ? normalized.split(' ').filter(Boolean) : [];
}

function isOrderTaskType(typeId: string): typeId is OrderTypeId {
  return ORDER_TYPE_IDS.has(typeId as OrderTypeId);
}

function isKnownVisitType(typeId: string): typeId is VisitTypeId {
  return (
    !isOrderTaskType(typeId) &&
    Object.prototype.hasOwnProperty.call(TASK_TYPE_DISCIPLINE_MAP, typeId)
  );
}

function therapyDisciplineForType(
  typeId: VisitTypeId
): TherapyDiscipline | null {
  const discipline = TASK_TYPE_DISCIPLINE_MAP[typeId];
  return discipline === 'PT' || discipline === 'OT' || discipline === 'ST'
    ? discipline
    : null;
}

function buildVisitAliases(
  typeId: VisitTypeId,
  discipline: DisciplineType
): string[] {
  const aliases: string[] = [typeId];

  if (typeId.startsWith('OASIS_')) {
    aliases.push('OASIS');
    const suffix = typeId.replace(/^OASIS_E2_/, '');
    const normalizedSuffix = suffix
      .replace(/_ENHANCED$/, '')
      .replace(/_(PT|OT|ST)$/, '');
    if (OASIS_SUFFIX_ALIAS[normalizedSuffix]) {
      aliases.push(OASIS_SUFFIX_ALIAS[normalizedSuffix]);
    }
  }

  if (discipline === 'SN') {
    aliases.push('SN', 'NURSING');
  } else if (discipline === 'MSW') {
    aliases.push('MSW', 'MEDICAL SOCIAL WORK', 'SOCIAL WORK');
  } else if (discipline === 'HHA') {
    aliases.push('HHA', 'HOME HEALTH AIDE', 'AIDE');
  } else {
    aliases.push('THERAPY', 'ALL THERAPY', discipline);
  }

  return aliases;
}

function buildMiscAliases(typeId: string | null | undefined): string[] {
  const aliases = ['MISC', 'MISCELLANEOUS'];
  return typeId ? [typeId, ...aliases] : aliases;
}

function buildOrderAliases(typeId: OrderTypeId): string[] {
  const aliases: string[] = [typeId, 'ORDER', 'ORDERS'];
  if (typeId.includes('485')) {
    aliases.push('485');
  }
  return aliases;
}

export const COMMUNICATION_TASK_TYPE_OPTIONS: ReadonlyArray<CreatableTaskTypeOption> =
  Object.freeze(
    PATIENT_COMMUNICATION_TYPES.map(type => ({
      id: type,
      display_name: type,
      taskKind: 'communication' as const,
    }))
  );

export const MISC_TASK_TYPE_OPTIONS: ReadonlyArray<CreatableTaskTypeOption> =
  Object.freeze(
    MISC_TASK_TYPES.map(type => ({
      id: type,
      display_name: type,
      taskKind: 'misc' as const,
    }))
  );

export function createCreatableTaskTypeOptions(
  visitTypeOptions: TaskTypeOption[]
): CreatableTaskTypeOption[] {
  return [
    ...withAliasOptions(visitTypeOptions)
      .filter(option => isCreatableTaskTypeOption(option))
      .map(option => ({
        ...option,
        taskKind: 'visit_or_order' as const,
      })),
    ...COMMUNICATION_TASK_TYPE_OPTIONS,
    ...MISC_TASK_TYPE_OPTIONS,
  ];
}

export function getTaskTypeClassification(
  typeId: string
): TaskTypeClassification {
  if (isOrderTaskType(typeId)) {
    return {
      category: 'orders',
      therapyDiscipline: null,
      aliases: buildOrderAliases(typeId),
    };
  }

  if (isKnownVisitType(typeId)) {
    const visitTypeId = typeId as VisitTypeId;
    const discipline = TASK_TYPE_DISCIPLINE_MAP[visitTypeId];
    const therapyDiscipline = therapyDisciplineForType(visitTypeId);

    // CC60 is care-coordination paperwork, not a clinical nursing note, so it
    // is filed under Comms even though its discipline is SN (the discipline
    // still drives its rate and billing seed). This override has to come
    // before the discipline checks below, which would otherwise file it under
    // Nursing.
    if (visitTypeId === 'CASE_CONFERENCE_60_DAY_SUMMARY') {
      return {
        category: 'comm',
        therapyDiscipline: null,
        aliases: buildVisitAliases(visitTypeId, discipline),
      };
    }

    if (therapyDiscipline !== null) {
      return {
        category: 'therapy',
        therapyDiscipline,
        aliases: buildVisitAliases(visitTypeId, therapyDiscipline),
      };
    }

    if (discipline === 'SN') {
      return {
        category: 'nursing',
        therapyDiscipline: null,
        aliases: buildVisitAliases(visitTypeId, discipline),
      };
    }

    if (discipline === 'HHA') {
      return {
        category: 'hha',
        therapyDiscipline: null,
        aliases: buildVisitAliases(visitTypeId, discipline),
      };
    }

    if (discipline === 'MSW') {
      return {
        category: 'msw',
        therapyDiscipline: null,
        aliases: buildVisitAliases(visitTypeId, discipline),
      };
    }

    return {
      category: 'visit',
      therapyDiscipline: null,
      aliases: buildVisitAliases(visitTypeId, discipline),
    };
  }

  return {
    category: 'unknown',
    therapyDiscipline: null,
    aliases: [typeId],
  };
}

export function getCreatableTaskTypeClassification(
  option: CreatableTaskTypeOption
): TaskTypeClassification {
  if (option.taskKind === 'communication') {
    return {
      category: 'comm',
      therapyDiscipline: null,
      aliases: [option.id, 'COMM', 'COMMS', 'COMMUNICATION'],
    };
  }

  if (option.taskKind === 'misc') {
    return {
      category: 'misc',
      therapyDiscipline: null,
      aliases: buildMiscAliases(option.id),
    };
  }

  return getTaskTypeClassification(optionTypeId(option));
}

function matchesSearch(
  value: string,
  search: string,
  aliases: string[]
): boolean {
  const searchTokens = getSearchableTokens(search);
  if (searchTokens.length === 0) {
    return true;
  }

  const candidates = [value, ...aliases].flatMap(candidate =>
    getSearchableTokens(candidate)
  );

  return searchTokens.every(token =>
    candidates.some(candidate =>
      EXACT_SEARCH_TOKENS.has(token)
        ? candidate === token
        : candidate.startsWith(token)
    )
  );
}

function matchesClassification(
  classification: TaskTypeClassification,
  filter: TaskTypeFilterValue
): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'nursing':
      return classification.category === 'nursing';
    case 'all_therapy':
      return classification.category === 'therapy';
    case 'pt':
      return (
        classification.category === 'therapy' &&
        classification.therapyDiscipline === 'PT'
      );
    case 'ot':
      return (
        classification.category === 'therapy' &&
        classification.therapyDiscipline === 'OT'
      );
    case 'st':
      return (
        classification.category === 'therapy' &&
        classification.therapyDiscipline === 'ST'
      );
    case 'hha':
      return classification.category === 'hha';
    case 'msw':
      return classification.category === 'msw';
    case 'orders':
      return classification.category === 'orders';
    case 'comm':
      return classification.category === 'comm';
    case 'misc':
      return classification.category === 'misc';
  }
}

export function getTaskRowClassification(
  task: TaskListResponse
): TaskTypeClassification {
  if (task.task_type === 'order') {
    return {
      category: 'orders',
      therapyDiscipline: null,
      aliases: [task.task_type, 'ORDER'],
    };
  }

  if (task.task_type === 'patient_communication') {
    return {
      category: 'comm',
      therapyDiscipline: null,
      aliases: [task.task_type, 'COMM'],
    };
  }

  if (task.task_type === 'misc_task') {
    return {
      category: 'misc',
      therapyDiscipline: null,
      aliases: buildMiscAliases(task.misc_task_type),
    };
  }

  if (task.visit_type) {
    return getTaskTypeClassification(task.visit_type);
  }

  if (task.task_type === 'visit') {
    return {
      category: 'visit',
      therapyDiscipline: null,
      aliases: [task.task_type, 'VISIT'],
    };
  }

  return {
    category: 'unknown',
    therapyDiscipline: null,
    aliases: [task.task_type],
  };
}

const VISIT_TASK_ROW_CATEGORIES = new Set<TaskTypeClassificationCategory>([
  'nursing',
  'therapy',
  'msw',
  'hha',
  'visit',
]);

/** True when the episode task row represents a visit (not order, comm, or unknown). */
export function isVisitTaskRow(task: TaskListResponse): boolean {
  // A row carrying a visit_type is a visit regardless of the tab it files
  // under: CC60 is categorised as 'comm' but is still a real visit row.
  if (task.task_type === 'visit' && task.visit_type) {
    return true;
  }
  return VISIT_TASK_ROW_CATEGORIES.has(getTaskRowClassification(task).category);
}

export function filterVisitTypeOptions(
  options: TaskTypeOption[],
  filter: TaskTypeFilterValue,
  searchQuery?: string
): TaskTypeOption[] {
  return options.filter(option => {
    if (!isCreatableTaskTypeOption(option)) {
      return false;
    }

    const classification = getTaskTypeClassification(optionTypeId(option));

    return (
      matchesClassification(classification, filter) &&
      matchesSearch(
        `${option.id} ${option.display_name}`,
        searchQuery ?? '',
        classification.aliases
      )
    );
  });
}

export const DEFAULT_NEW_TASK_TYPE: TaskTypeId = 'RN_SKILLED_NURSING_VISIT';

const PREFERRED_ADD_TASK_OPTION_BY_FILTER: Record<
  TaskTypeFilterValue,
  CreatableTaskTypeOption['id']
> = {
  all: DEFAULT_NEW_TASK_TYPE,
  nursing: DEFAULT_NEW_TASK_TYPE,
  all_therapy: 'PT_VISIT',
  pt: 'PT_VISIT',
  ot: 'OT_VISIT',
  st: 'ST_VISIT',
  hha: 'HHA_VISIT',
  msw: 'MSW_VISIT',
  orders: 'PHYSICIAN_ORDER',
  comm: 'Patient Communication',
  misc: 'Other Documents',
};

export function getDefaultCreatableTaskTypeOptionId(
  options: CreatableTaskTypeOption[],
  filter: TaskTypeFilterValue,
  fallback: CreatableTaskTypeOption['id'] = DEFAULT_NEW_TASK_TYPE
): CreatableTaskTypeOption['id'] {
  const preferredId = PREFERRED_ADD_TASK_OPTION_BY_FILTER[filter] ?? fallback;

  if (filter === DEFAULT_TASK_TYPE_FILTER) {
    return (
      options.find(option => option.id === preferredId)?.id ??
      options.find(option => option.id === fallback)?.id ??
      fallback
    );
  }

  const filteredOptions = filterCreatableTaskTypeOptions(options, filter);
  const preferredMatch = filteredOptions.find(
    option => option.id === preferredId
  );

  if (preferredMatch) {
    return preferredMatch.id;
  }

  if (filteredOptions.length > 0) {
    return filteredOptions[0].id;
  }

  return preferredId;
}

export function filterCreatableTaskTypeOptions(
  options: CreatableTaskTypeOption[],
  filter: TaskTypeFilterValue,
  searchQuery?: string
): CreatableTaskTypeOption[] {
  return options.filter(option => {
    if (
      option.taskKind !== 'communication' &&
      option.taskKind !== 'misc' &&
      !isCreatableTaskTypeOption(option)
    ) {
      return false;
    }

    const classification = getCreatableTaskTypeClassification(option);

    return (
      matchesClassification(classification, filter) &&
      matchesSearch(
        `${option.id} ${option.display_name}`,
        searchQuery ?? '',
        classification.aliases
      )
    );
  });
}

export function filterVisitOnlyTypeOptions(
  options: TaskTypeOption[]
): TaskTypeOption[] {
  return options.filter(option => {
    const category = getTaskTypeClassification(optionTypeId(option)).category;
    return category !== 'orders' && isCreatableTaskTypeOption(option);
  });
}

export function filterTaskRows<T extends TaskListResponse>(
  rows: T[],
  filter: TaskTypeFilterValue
): T[] {
  return rows.filter(row =>
    matchesClassification(getTaskRowClassification(row), filter)
  );
}
